import { useState, useEffect } from 'react';
import PageHero from '../components/layout/PageHero';
import SEO from '../components/SEO';
import SafecareBrand from '../components/SafecareBrand';
// static import removed
import './TheBill.css';

const helperParseLinks = (text, references) => {
    if (!text || !references) return text;
    const parts = text.split(/(\[[^\]]+\]\[\d+\])/g);
    return parts.map((part, i) => {
        const match = part.match(/^\[([^\]]+)\]\[(\d+)\]$/);
        if (match) {
            const [_, linkText, refId] = match;
            const ref = references.find(r => r.id === refId);
            if (ref) {
                return (
                    <span key={i} className="citation-wrapper">
                        <a href={ref.url} target="_blank" rel="noopener noreferrer" className="legal-citation-link">
                            {linkText}
                        </a>
                    </span>
                );
            }
        }
        return part;
    });
};

/** Recursively renders a node in the bill structure */
function BillNode({ node, references, depth = 0 }) {
    const depthClass = `bill-node bill-node-depth-${depth}`;
    return (
        <div className={depthClass}>
            <div className="bill-node-content">
                {node.marker && <span className="bill-node-marker">{node.marker}</span>}
                {node.text && (
                    <span className="bill-node-text">
                        {helperParseLinks(node.text, references)}
                    </span>
                )}
            </div>
            {Array.isArray(node.children) && node.children.length > 0 && (
                <div className="bill-node-children">
                    {node.children.map((child, i) => (
                        <BillNode key={i} node={child} references={references} depth={depth + 1} />
                    ))}
                </div>
            )}
        </div>
    );
}

/** Renders one top-level section */
function BillSection({ section, references }) {
    return (
        <div className="bill-section">
            <div className="bill-section-header">
                <span className="bill-section-number">SEC. {section.sectionNumber}.</span>
                <span className="bill-section-heading">{section.heading}</span>
            </div>

            {section.rawText && !section.structure && (
                <div className="section-raw-text">
                    {section.rawText.split('\n').map((line, i) => (
                        <p key={i}>{helperParseLinks(line, references)}</p>
                    ))}
                </div>
            )}

            {Array.isArray(section.structure) && (
                <div className="bill-section-structure">
                    {section.structure.map((node, i) => (
                        <BillNode key={i} node={node} references={references} depth={0} />
                    ))}
                </div>
            )}
        </div>
    );
}

/** Renders a Bill Title (TITLE I, etc) */
function BillTitle({ title, references }) {
    const isPreTitle = title.id === 'PRE_TITLE';

    return (
        <div className={`bill-title-block ${isPreTitle ? 'pre-title' : ''}`}>
            {!isPreTitle && (
                <div className="bill-title-header">
                    {title.roman && <span className="title-roman">TITLE {title.roman}—</span>}
                    <span className="title-name">{title.name}</span>
                </div>
            )}

            <div className="bill-title-sections">
                {title.sections.map((section, idx) => (
                    <BillSection key={idx} section={section} references={references} />
                ))}
            </div>
        </div>
    );
}

export default function TheBill() {
    const [billData, setBillData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch('/data/bill_content.json')
            .then(res => res.json())
            .then(data => {
                setBillData(data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Failed to load bill content", err);
                setLoading(false);
            });
    }, []);

    if (loading) return <div style={{ textAlign: 'center', padding: '10vh', color: '#fff' }}>Loading Legislative Text...</div>;
    if (!billData) return null;

    const references = billData.references || [];

    return (
        <div className="bill-page">
            <SEO
                title="The SAFEC+ Act - Full Legislative Text"
                description="Read the complete SAFEC+ Act. Comprehensive healthcare reform legislation with detailed provisions and requirements."
                image="/image/issue_security.jpg"
            />
            <PageHero
                title="The Legislation"
                subtitle="Read the proposed text of the Secure Affordable Federal Essential Care Act Plus."
                image="/image/issue_security.jpg"
                badge="Full Text"
            />

            <div className="bill-container">
                <div className="bill-paper">
                    {/* Header */}
                    <div className="bill-header">
                        <div className="congress-session">
                            <p>{billData.congressSession?.split('\n').map((line, i) => <span key={i}>{line}<br /></span>)}</p>
                        </div>
                        <div className="bill-number">
                            <h1>{billData.billTitle}</h1>
                        </div>
                        <div className="bill-long-title">
                            <p>{billData.longTitle}</p>
                        </div>
                    </div>

                    {/* Intro */}
                    <div className="bill-intro">
                        <p>{billData.intro?.split('\n').map((line, i) => <span key={i}>{line}<br /></span>)}</p>
                    </div>

                    {/* Hierarchy: Titles -> Sections -> Structure Nodes */}
                    <div className="bill-body">
                        {billData.titles?.map((title, idx) => (
                            <BillTitle key={idx} title={title} references={references} />
                        ))}
                    </div>

                    {/* References Footer */}
                    {references.length > 0 && (
                        <div className="bill-footer">
                            <h3>References & Citations</h3>
                            <div className="references-grid">
                                {references.map(ref => (
                                    <div key={ref.id} className="reference-item">
                                        <span className="ref-id">[{ref.id}]</span>
                                        <span className="ref-text">{ref.text}</span>
                                        {ref.url && (
                                            <a href={ref.url} target="_blank" rel="noopener noreferrer" className="ref-link">
                                                View Source
                                            </a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
